import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import { Checkbox, SxProps, Theme } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { DayOfWeek, getDaysOfWeek } from "../../Configuration/utils";
import { CustomAutocomplete } from "../../components/Inputs/CustomAutocomplete";
import { GenericObject } from "../../services/Price";
import { DayOfWeekType } from "../interfaces";
import CustomRenderTags from "./CustomRenderTags";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

/**
 * Returns a list of weekdays with the checked attribute
 *
 * It is used with a select multiple.
 * Object.keys only returns string, so we need to convert the keys
 * back to number and then cast it to DayOfWeek.
 *
 * TODO: try to merge the behaviour with useGetDaysOfWeekList
 */
export const useGetDaysOfWeekList2 = () => {
  const days = getDaysOfWeek();
  const [list] = useState<Array<DayOfWeekType>>(
    Object.keys(days)
      .map((key) => parseInt(key) as DayOfWeek)
      .map((key, index) => ({
        id: index + 1,
        checked: false,
        label: days[key],
        value: key,
      }))
  );
  return list;
};

/**
 * Allows the selection of one or more weekdays
 * @param label
 * @param placeholder
 * @param onChange
 * @param value
 * @param isFilter
 * @param sx
 * @constructor
 */
const DayOfWeekSelector = ({
  label,
  placeholder,
  onChange,
  value,
  isFilter,
  sx,
}: {
  label?: string;
  placeholder?: string;
  onChange: (value: number[]) => void;
  value: DayOfWeek[];
  isFilter?: boolean;
  sx?: SxProps<Theme>;
}) => {
  const intl = useIntl();
  const limitTags = 2;
  const days = useGetDaysOfWeekList2();
  const [options, setOptions] = useState<DayOfWeekType[]>(
    days.map((el) => ({ ...el, checked: false }))
  );
  const [selectedDays, setSelectedDays] = useState<DayOfWeekType[]>([]);

  const handleDeleteTag = (deleteItem: number[]) => {
    onChange(value.filter((el) => !deleteItem.includes(el)));
  };

  /**
   * Updates both options and value when the selection change
   *
   * Options must be updated so that we now which is already selected or not.
   * @param event
   * @param value
   */
  const handleChange = (
    event: React.SyntheticEvent,
    value: DayOfWeekType[]
  ) => {
    if (
      event.type === "keydown" &&
      (event as React.KeyboardEvent).key === "Backspace"
    )
      return;

    const newOptions: DayOfWeekType[] = options.map((day) => ({
      ...day,
      checked: !!value.find((el) => el.value === day.value),
    }));
    const newValue: DayOfWeek[] = value.map(({ value }) => value);

    setOptions(newOptions);
    onChange(newValue);
  };

  /**
   * Sets the selected days from the input value
   *
   * Because the input and the data we display are different
   * we need a state to keep the selected values for the autocomplete.
   */
  useEffect(() => {
    setSelectedDays(options.filter((el) => value.includes(el.value)));
  }, [options, value]);

  return (
    <CustomAutocomplete
      disableClearable={isFilter ? true : false}
      disableCloseOnSelect
      formStyle={false}
      getOptionLabel={(option: DayOfWeekType) => option.label}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      slotProps={{
        // Props for list popover
        listbox: {
          style: {
            // Used to force height and delete scroll (36px is option height and 7 is number of days in a week)
            minHeight: "calc(36px*7)",
          },
        },
      }}
      limitTags={limitTags}
      multiple
      noOptionsText={intl.formatMessage({ id: "NO_DATA" })}
      options={options}
      onChange={handleChange}
      label={label}
      placeholder={placeholder}
      textFieldSx={{
        margin: "auto 0",
        "& .MuiOutlinedInput-root.MuiInputBase-sizeSmall": {
          paddingTop: 0,
          paddingBottom: 0,
        },
      }}
      renderOption={(props, option: GenericObject, { selected }) => (
        <li {...props} key={option.label}>
          <Checkbox
            icon={icon}
            checkedIcon={checkedIcon}
            sx={{ mr: 1, p: 0 }}
            checked={selected}
          />
          {option.label}
        </li>
      )}
      renderValue={(value, getItemProps) => (
        <CustomRenderTags
          value={value}
          getTagProps={getItemProps}
          label={
            selectedDays.length > 1
              ? intl.formatMessage({ id: "DAYS" })
              : intl.formatMessage({ id: "DAY" })
          }
          limitTagsChip={limitTags}
          isFilter={isFilter}
          onDelete={handleDeleteTag}
        />
      )}
      size="small"
      sx={[
        {
          "& .MuiChip-root": {
            height: "20px",
            mx: 0.25,
            borderRadius: "2px",
            "& span": {
              fontSize: "0.8125rem",
            },
            "& svg": {
              width: "12px",
            },
          },
        },
        // sx can be an object or an array of objects
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      value={selectedDays}
    />
  );
};

export default DayOfWeekSelector;
